// frontend/src/components/JournalEditor/JournalEditorWrapper.js

import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TemplateContext } from '../../context/TemplateContext';
import JournalEditor from './JournalEditor';
import apiClient from '../../apiClient';
import streamClient from '../../streamClient';

function JournalEditorWrapper() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const completedParam = searchParams.get('completed') === '1';
  
  const { templateId, setTemplateId } = useContext(TemplateContext);
  
  const [sessionData, setSessionData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [generatedSections, setGeneratedSections] = useState([]);
  const [streamingSections, setStreamingSections] = useState([]);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pending journal text - väntar på att template ska laddas innan parsing
  const [pendingJournalText, setPendingJournalText] = useState(null);

  // Klinik-inställningar för patientjournal
  const [clinicSettings, setClinicSettings] = useState({
    use_internal_journal: false,
    journal_system: 'Kaddio'
  });
  // Ingen patient från sessionen — patientval sker i editorn (EHR-flödet)
  const [patientId] = useState(null);

  // Fetch clinic settings
  useEffect(() => {
    const fetchClinicSettings = async () => {
      try {
        const resp = await apiClient.get('/api/journal/clinic/settings');
        setClinicSettings({
          use_internal_journal: resp.data.use_internal_journal || false,
          journal_system: resp.data.journal_system || 'Kaddio'
        });
      } catch (error) {
        console.error('Error fetching clinic settings:', error);
      }
    };

    fetchClinicSettings();
  }, []);

  // Fetch templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const resp = await apiClient.get('/api/journalmallar');
        if (Array.isArray(resp.data)) {
          setTemplates(resp.data);
        }
      } catch (error) {
        console.error('Error fetching templates:', error);
        setError('Kunde inte hämta journalmallar');
      }
    };

    fetchTemplates();
  }, []);

  // Fetch session data
  useEffect(() => {
    if (!sessionId) {
      setError('Inget sessions-ID angivet');
      setLoading(false);
      return;
    }

    const fetchSessionData = async () => {
      try {
        const resp = await apiClient.get(`/recorder/sessions/${sessionId}`);
        const session = resp.data;
        setSessionData(session);

        // Set template from session
        if (session.template_id) {
          setTemplateId(session.template_id);
        }

        // Spara journaltext och vänta på att template laddas innan parsing
        console.log('Session data:', session);
        if (session.generated_journal) {
          console.log('Generated journal text:', session.generated_journal);
          // Spara journaltext - parsas i separat useEffect när template är redo
          setPendingJournalText(session.generated_journal);
        } else {
          console.log('No generated_journal found in session');
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching session:', error);
        setError('Kunde inte hämta session');
        setLoading(false);
      }
    };

    fetchSessionData();
  }, [sessionId, setTemplateId]);

  // Set selected template when templateId or templates change
  useEffect(() => {
    if (templateId && templates.length > 0) {
      const template = templates.find(t => t.template_id === parseInt(templateId));
      if (template) {
        setSelectedTemplate(template);
      }
    }
  }, [templateId, templates]);

  // ===========================================================
  //   Parsa pending journal när template är redo
  // ===========================================================
  useEffect(() => {
    // Vänta tills både journaltext och template finns
    if (!pendingJournalText || !selectedTemplate) return;

    console.log('Parsing pending journal with template:', selectedTemplate.template_name);
    const secs = parseGeneratedJournal(pendingJournalText, selectedTemplate);
    console.log('Parsed sections:', secs);
    setGeneratedSections(secs);

    // Rensa pending text så vi inte parsar igen
    setPendingJournalText(null);
  }, [pendingJournalText, selectedTemplate]);

  const parseGeneratedJournal = (journalText, template = null) => {
    /**
     * UPDATED: Matches parseJournalNote from MainContent/TextToJournal
     * Frontend trusts backend (Structured Outputs)
     *
     * @param template - Optional template to use instead of selectedTemplate (fixes race condition)
     */
    if (!journalText || !journalText.trim()) {
      return [];
    }

    // Använd medskickad template eller fallback till selectedTemplate
    const templateToUse = template || selectedTemplate;

    // Hämta tillåtna rubriker från template (för parsing)
    const allowedTitles = new Set();
    if (templateToUse?.sections) {
      templateToUse.sections.forEach(sec => {
        allowedTitles.add(sec.section_title.trim());
      });
    }

    const lines = journalText.split('\n');
    const secs = [];
    let current = null;
    let i = 0;

    while (i < lines.length) {
      const line = lines[i].trim();

      if (line) {
        const isValidTitle = allowedTitles.size === 0 || allowedTitles.has(line);

        if (isValidTitle && !current) {
          // Starta ny sektion
          current = { title: line, content: '' };
          i++;

          // Hoppa över tomma rader efter rubrik
          while (i < lines.length && lines[i]?.trim() === '') i++;

          // Samla innehåll tills nästa rubrik eller tom rad
          while (i < lines.length) {
            const nextLine = lines[i].trim();

            // Ny rubrik? Avbryt (konsekvent med isValidTitle-logiken ovan)
            const isNextValidTitle = allowedTitles.size === 0 || allowedTitles.has(nextLine);
            if (nextLine && isNextValidTitle) {
              break;
            }

            // Tom rad efter innehåll? Avbryt
            if (!nextLine && current.content.trim()) {
              i++;
              break;
            }

            current.content += lines[i] + '\n';
            i++;
          }

          secs.push(current);
          current = null;
        } else if (current) {
          current.content += line + '\n';
          i++;
        } else {
          // Text utan rubrik
          if (secs.length === 0) {
            secs.push({ title: 'Okänt', content: line + '\n' });
          } else {
            secs[secs.length - 1].content += line + '\n';
          }
          i++;
        }
      } else {
        i++;
      }
    }

    return secs;
  };

  const handleUpdateGeneratedSections = (newSections) => {
    setGeneratedSections(newSections);
  };

  const handleRegenerate = async (templateOverride = null) => {
    /**
     * @param templateOverride - Optional template to use (fixes race condition when changing templates)
     */
    const templateToUse = templateOverride || selectedTemplate;

    if (!sessionData?.transcription_text || !templateToUse) {
      setError('Ingen transkription eller mall tillgänglig');
      return;
    }

    setIsTranscribing(true);
    setStreamingSections([]);
    setGeneratedSections([]);

    try {
      // Backend streaming (samlar hela svaret)
      let accumulated = '';

      await streamClient.post(
        '/api/chatgpt/generate_journal',
        {
          text: sessionData.transcription_text,
          template_id: templateToUse.template_id,
          session_id: sessionId
        },
        (chunk) => {
          accumulated += chunk;
          const partial = parseGeneratedJournal(accumulated, templateToUse);
          setStreamingSections(partial);
        }
      );

      // Parse final result
      const finalSecs = parseGeneratedJournal(accumulated.trim(), templateToUse);

      // Sätt sektioner direkt (ingen fake animation)
      setGeneratedSections(finalSecs);
      setStreamingSections([]);
      setIsTranscribing(false);

      // Spara till backend
      if (sessionId) {
        const combinedText = finalSecs
          .map((sec) => `${sec.title}\n${sec.content.trim()}`)
          .join('\n\n');

        await apiClient.patch(`/recorder/sessions/${sessionId}/update_journal`, {
          generated_journal: combinedText,
        });
      }
    } catch (err) {
      console.error('Fel vid regenerering:', err);
      setIsTranscribing(false);
      setError('Fel vid regenerering av journal. Försök igen.');
    }
  };

  // State för att trigga regenerering efter template-byte
  const [triggerRegeneration, setTriggerRegeneration] = useState(null);

  const handleChangeTemplate = async (newTemplateId) => {
    const template = templates.find(t => t.template_id === parseInt(newTemplateId));
    if (!template) {
      setError('Mall ej hittad');
      return;
    }

    // Uppdatera sessionen i backend FÖRST
    if (sessionId) {
      await apiClient.patch(
        `/recorder/sessions/${sessionId}/update_template`,
        { template_id: newTemplateId }
      );
    }

    // Uppdatera state
    setSelectedTemplate(template);
    setTemplateId(newTemplateId);

    // Trigga regenerering med template efter att state har uppdaterats
    // Detta sker i useEffect nedan
    if (sessionData?.transcription_text) {
      setTriggerRegeneration(template);
    }
  };

  // useEffect som lyssnar på triggerRegeneration och kör regenerering
  // Detta säkerställer att selectedTemplate har uppdaterats innan regenerering
  //
  // VARFÖR DETTA ÄR NÖDVÄNDIGT:
  // När användaren byter mall via dropdown:
  // 1. setSelectedTemplate(newTemplate) är async (queued)
  // 2. Om vi kör handleRegenerate direkt, läser useJournalSections OLD selectedTemplate
  // 3. prepareSections validerar NYA sections mot GAMLA mallens rubriker
  // 4. Resultat: "ogiltiga" rubriker filtreras bort eller läggs som underrubriker
  //
  // MED DENNA LÖSNING:
  // 1. setSelectedTemplate(newTemplate) uppdateras
  // 2. React renderar med nya selectedTemplate
  // 3. useEffect triggas EFTER render
  // 4. handleRegenerate körs med selectedTemplate redan uppdaterad
  // 5. useJournalSections får NYA sections OCH NYA selectedTemplate samtidigt
  // 6. prepareSections validerar korrekt ✓
  useEffect(() => {
    if (triggerRegeneration) {
      // Körs i nästa render cycle när selectedTemplate garanterat är uppdaterad
      handleRegenerate(triggerRegeneration);
      setTriggerRegeneration(null);  // Reset trigger
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerRegeneration]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Laddar journal...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ color: 'red' }}>{error}</div>
      </div>
    );
  }

  return (
    <JournalEditor
      sessionId={sessionId}
      inputText={sessionData?.transcription_text || ''}
      selectedTemplate={selectedTemplate}
      templates={templates}
      streamingSections={streamingSections}
      generatedSections={generatedSections}
      isTranscribing={isTranscribing}
      onAdjust={() => {}}
      onRegenerate={handleRegenerate}
      onChangeTemplate={handleChangeTemplate}
      onUpdateGeneratedSections={handleUpdateGeneratedSections}
      startDateTime={sessionData?.created_at ? new Date(sessionData.created_at) : null}
      // Nya props för patientjournal-integration
      patientId={patientId}
      useInternalJournal={clinicSettings.use_internal_journal}
      onJournalSaved={(entryId) => {
        console.log('Journal saved with entry_id:', entryId);
      }}
      transcriptionUpdatedAfterJournal={sessionData?.transcription_updated_after_journal || false}
    />
  );
}

export default JournalEditorWrapper;